/**
 * nh-cart.js — Nanou's House cart helpers (loaded as a module on every page by sections/nh-gift-tiers).
 *
 * 1. Cart sync: wraps window.fetch once; after any request to /cart/add|change|update|clear
 *    it fetches /cart.js and dispatches `nh:cart-changed` (detail = cart) on document.
 *    Horizon updates the cart with fetch, so this works without touching Horizon's JS.
 *    Shared on window: nhFetch (unwrapped fetch), nhRefreshCart(), nhRenderDrawer(), nhWaitForHorizon().
 * 2. <nh-shipping-bar>: old free-delivery bar (kept for rollback; replaced by <nh-gift-bar>, see nh-gifts.js).
 * 3. <nh-cart-upsell>: "Complétez votre panier" in the cart drawer, from Shopify product
 *    recommendations of the first cart item; "+ Ajouter" adds to cart and re-renders the drawer.
 *    Gift products (tag nh-cadeau, 0 DH) are never suggested.
 *    If Shopify refuses the add (e.g. a pack whose last units are already in the cart), the card says
 *    so and goes away, the drawer is NOT redrawn (no jump to the top), and that product is not
 *    suggested again during the visit (ADR-042).
 */

const CART_URL = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

if (!window.__nhCartPatched) {
  window.__nhCartPatched = true;
  const originalFetch = window.fetch.bind(window);
  window.nhFetch = originalFetch;
  window.fetch = async (...args) => {
    const response = await originalFetch(...args);
    try {
      const url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
      if (CART_URL.test(url)) setTimeout(nhRefreshCart, 60);
    } catch (e) {}
    return response;
  };
}

export async function nhRefreshCart() {
  const cart = await window.nhFetch(`${root}cart.js`).then((r) => r.json());
  window.nhLastCart = cart;
  document.dispatchEvent(new CustomEvent('nh:cart-changed', { detail: cart }));
  return cart;
}

/** Resolves when Horizon has finished its own cart updates (quantity change / remove + redraw). */
export async function nhWaitForHorizon() {
  const CartItems = customElements.get('cart-items-component');
  const pending = (CartItems && CartItems.pendingQuantityUpdates) || [];
  if (pending.length) await Promise.allSettled(pending);
}

/**
 * Re-draws the cart (drawer and cart page) after WE changed the cart, with Horizon's own section
 * renderer (same call Horizon uses), so its components and animations stay intact. Never replaces
 * the drawer DOM by hand: doing so while Horizon was redrawing emptied the item list (ADR-026).
 */
export async function nhRenderDrawer() {
  await nhWaitForHorizon();
  const cart = window.nhLastCart;
  if (cart) document.querySelectorAll('.cart-bubble__text-count').forEach((el) => (el.textContent = cart.item_count));
  const lists = [...document.querySelectorAll('cart-items-component[data-section-id]')];
  if (!lists.length) return;
  try {
    const { sectionRenderer } = await import('@theme/section-renderer');
    const done = new Set();
    for (const list of lists) {
      const id = list.dataset.sectionId;
      if (done.has(id)) continue;
      done.add(id);
      await sectionRenderer.renderSection(id, { cache: false, mode: list.dataset.drawer !== undefined ? 'hydration' : 'full' });
    }
    // Ask Horizon's drawer to measure its sticky summary again (same event Horizon uses after a restore).
    const { CartSectionRestoredEvent } = await import('@theme/events');
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => el.dispatchEvent(new CartSectionRestoredEvent()));
  } catch (e) {
    if (/\/cart\/?$/.test(window.location.pathname)) window.location.reload();
  }
  // Our own blocks inside the redrawn section: draw them again with the current cart.
  document.querySelectorAll('nh-cart-upsell').forEach((el) => el.load && el.load());
  if (cart) document.dispatchEvent(new CustomEvent('nh:cart-redrawn', { detail: cart }));
}

window.nhRefreshCart = nhRefreshCart;
window.nhRenderDrawer = nhRenderDrawer;
window.nhWaitForHorizon = nhWaitForHorizon;

// Also refresh on Horizon's own cart event and when the page comes back from the browser cache,
// so the gift bar is always up to date whatever changed the cart.
if (!window.__nhCartEvents) {
  window.__nhCartEvents = true;
  document.addEventListener('cart:update', () => setTimeout(nhRefreshCart, 60));
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) nhRefreshCart();
  });
}

const formatMoney = (cents, format) =>
  (format || '{{amount}}').replace(/\{\{\s*\w+\s*\}\}/, (cents / 100).toFixed(2));

if (!customElements.get('nh-shipping-bar')) {
  class NhShippingBar extends HTMLElement {
    connectedCallback() {
      this.onCart = (e) => this.render(e.detail);
      document.addEventListener('nh:cart-changed', this.onCart);
    }
    disconnectedCallback() {
      document.removeEventListener('nh:cart-changed', this.onCart);
    }
    render(cart) {
      const threshold = Number(this.dataset.threshold);
      if (!threshold || !cart) return;
      const remaining = Math.max(threshold - cart.total_price, 0);
      const msg = this.querySelector('.nh-shipping-bar__msg');
      const fill = this.querySelector('.nh-shipping-bar__fill');
      if (msg) {
        msg.textContent =
          remaining > 0
            ? this.dataset.textRemaining.replace('[amount]', formatMoney(remaining, this.dataset.moneyFormat))
            : this.dataset.textReached;
      }
      if (fill) fill.style.width = Math.min((cart.total_price / threshold) * 100, 100) + '%';
      this.classList.toggle('nh-shipping-bar--reached', remaining === 0);
    }
  }
  customElements.define('nh-shipping-bar', NhShippingBar);
}

// Variants Shopify refused to add during this visit: never suggested again (survives drawer redraws).
const upsellRefused = new Set();

if (!customElements.get('nh-cart-upsell')) {
  class NhCartUpsell extends HTMLElement {
    connectedCallback() {
      this.load();
    }
    async load() {
      const list = this.querySelector('.nh-upsell__list');
      const cart = await window.nhFetch(`${root}cart.js`).then((r) => r.json()).catch(() => null);
      if (!cart || !cart.items.length || !list) return;
      const inCart = new Set(cart.items.map((i) => i.product_id));
      const limit = Number(this.dataset.limit) || 3;
      let products = [];
      for (const intent of ['complementary', 'related']) {
        const url = `${root}recommendations/products.json?product_id=${cart.items[0].product_id}&limit=10&intent=${intent}`;
        const data = await window.nhFetch(url).then((r) => (r.ok ? r.json() : { products: [] })).catch(() => ({ products: [] }));
        products = products.concat(
          (data.products || []).filter(
            (p) =>
              !inCart.has(p.id) &&
              p.available &&
              p.price > 0 &&
              !(p.tags || []).includes('nh-cadeau') &&
              !(p.variants || []).some((v) => upsellRefused.has(String(v.id)))
          )
        );
        if (products.length >= limit) break;
      }
      const seen = new Set();
      products = products.filter((p) => (seen.has(p.id) ? false : seen.add(p.id))).slice(0, limit);
      list.innerHTML = '';
      if (!products.length) return;
      products.forEach((p) => list.append(this.card(p)));
      this.hidden = false;
    }
    card(p) {
      const li = document.createElement('li');
      li.className = 'nh-upsell__item';
      const variant = p.variants.find((v) => v.available) || p.variants[0];
      const img = p.featured_image ? `${p.featured_image}${p.featured_image.includes('?') ? '&' : '?'}width=160` : '';
      li.innerHTML = `
        <a class="nh-upsell__media" href="${root}products/${p.handle}">${img ? `<img src="${img}" alt="" loading="lazy" width="64" height="64">` : ''}</a>
        <div class="nh-upsell__info">
          <a class="nh-upsell__title" href="${root}products/${p.handle}"></a>
          <span class="nh-upsell__price">${formatMoney(variant.price, this.dataset.moneyFormat)}</span>
        </div>
        <button type="button" class="nh-upsell__add">${this.dataset.addLabel}</button>`;
      li.querySelector('.nh-upsell__title').textContent = p.title;
      li.querySelector('.nh-upsell__add').addEventListener('click', (e) => this.add(variant.id, e.currentTarget));
      return li;
    }
    async add(variantId, button) {
      const label = button.textContent;
      button.disabled = true;
      button.textContent = '…';
      let status = 0;
      try {
        const res = await window.nhFetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
        });
        status = res.status;
      } catch (e) {}
      if (status >= 200 && status < 300) {
        await nhRefreshCart();
        await nhRenderDrawer();
        return;
      }
      // Refused by Shopify (422: not enough stock with what is already in the cart): say it, then drop the card.
      if (status === 422) {
        upsellRefused.add(String(variantId));
        this.refused(button.closest('.nh-upsell__item'));
        return;
      }
      // Network or other error: let the customer try again.
      button.disabled = false;
      button.textContent = label;
      this.note(button.closest('.nh-upsell__item'), this.dataset.retryLabel);
    }
    note(item, text) {
      if (!item || !text) return;
      let note = item.querySelector('.nh-upsell__note');
      if (!note) {
        note = document.createElement('span');
        note.className = 'nh-upsell__note';
        note.setAttribute('role', 'status');
        item.querySelector('.nh-upsell__info').append(note);
      }
      note.textContent = text;
    }
    refused(item) {
      if (!item) return;
      item.classList.add('nh-upsell__item--refused');
      item.querySelector('.nh-upsell__add')?.remove();
      item.querySelector('.nh-upsell__price')?.remove();
      this.note(item, this.dataset.stockLabel);
      setTimeout(() => {
        item.remove();
        if (!this.querySelector('.nh-upsell__item')) this.hidden = true;
      }, 4000);
    }
  }
  customElements.define('nh-cart-upsell', NhCartUpsell);
}
